/**
 * Header functionality
 */

export class Header {
  private header: HTMLElement | null;
  private mobileMenuToggle: HTMLElement | null;
  private mobileMenu: HTMLElement | null;
  private megaMenuContainers: NodeListOf<HTMLElement>;
  private activeMegaMenu: HTMLElement | null = null;
  private body: HTMLElement;
  private activeTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.header = document.querySelector('[data-header]');
    this.mobileMenuToggle = document.querySelector('[data-menu-toggle]');
    this.mobileMenu = document.querySelector('[data-mobile-menu]');
    this.megaMenuContainers = document.querySelectorAll('.mega-menu-container');
    this.body = document.body;

    if (this.header) {
      this.init();
    }
  }

  private init(): void {
    if (this.mobileMenuToggle) {
      this.mobileMenuToggle.addEventListener('click', () => this.toggleMobileMenu());
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.body.classList.contains('menu-open')) {
        this.closeMobileMenu();
      }
    });

    this.initMobileSubmenus();

    this.initMegaMenu();

    this.positionMobileMenu();
    window.addEventListener('resize', () => this.positionMobileMenu());

    this.initStickyHeader();
  }

  private toggleMobileMenu(): void {
    if (this.body.classList.contains('menu-open')) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  private openMobileMenu(): void {
    this.body.classList.add('menu-open');
    this.mobileMenuToggle?.classList.add('menu-open');
    this.mobileMenuToggle?.setAttribute('aria-expanded', 'true');
  }

  private closeMobileMenu(): void {
    this.body.classList.remove('menu-open');
    this.mobileMenuToggle?.classList.remove('menu-open');
    this.mobileMenuToggle?.setAttribute('aria-expanded', 'false');
  }

  private initMobileSubmenus(): void {
    const submenuParents = document.querySelectorAll('.mobile-menu .menu-item-has-children > a');
    
    submenuParents.forEach(parent => {
      const toggleBtn = document.createElement('button');
      toggleBtn.className = 'submenu-toggle absolute right-0 top-0 p-2 text-redi-blau w-10 h-10 flex items-center justify-center';
      toggleBtn.innerHTML = '<span class="text-xl leading-none">+</span>';
      toggleBtn.setAttribute('aria-label', 'Toggle submenu');
      
      const listItem = parent.parentElement;
      if (listItem) {
        listItem.classList.add('relative');
        listItem.appendChild(toggleBtn);
      }
      
      toggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (listItem?.classList.contains('is-open')) {
          listItem.classList.remove('is-open');
          toggleBtn.innerHTML = '<span class="text-xl leading-none">+</span>';
        } else {
          const siblings = listItem?.parentElement?.querySelectorAll(':scope > .menu-item-has-children.is-open');
          siblings?.forEach(sibling => {
            if (sibling !== listItem) {
              sibling.classList.remove('is-open');
              const siblingToggle = sibling.querySelector('.submenu-toggle');
              if (siblingToggle) {
                siblingToggle.innerHTML = '<span class="text-xl leading-none">+</span>';
              }
            }
          });
          
          listItem?.classList.add('is-open');
          toggleBtn.innerHTML = '<span class="text-xl leading-none">-</span>';
        }
      });
      
      parent.addEventListener('click', (e) => {
        if (parent.getAttribute('href') === '#') {
          e.preventDefault();
        }
      });
    });
  }

  private initMegaMenu(): void {
    const menuItems = document.querySelectorAll('.main-menu > li');
    
    menuItems.forEach(item => {
      const link = item.querySelector('a.has-mega-menu');
      
      if (link) {
        if (link.getAttribute('href') === '#') {
          link.addEventListener('click', (e) => {
            e.preventDefault();
          });
        }

        item.addEventListener('mouseenter', () => {
          if (this.activeTimeout) {
            clearTimeout(this.activeTimeout);
          }
          const menuId = link.getAttribute('data-mega-menu');
          this.showMegaMenu(menuId);
        });

        item.addEventListener('mouseleave', () => {
          this.activeTimeout = setTimeout(() => this.hideAllMegaMenus(), 100);
        });
      }
    });

    this.megaMenuContainers.forEach(container => {
      container.addEventListener('mouseenter', () => {
        if (this.activeTimeout) {
          clearTimeout(this.activeTimeout);
        }
      });

      container.addEventListener('mouseleave', () => {
        this.hideAllMegaMenus();
      });
    });
  }

  private showMegaMenu(menuId: string | null): void {
    if (!menuId) return;
    
    this.hideAllMegaMenus();
    
    const targetMenu = document.getElementById(`mega-menu-${menuId}`);
    if (targetMenu) {
      targetMenu.classList.remove('opacity-0', 'invisible');
      targetMenu.classList.add('opacity-100', 'visible');
      this.activeMegaMenu = targetMenu;
    }
  }

  private hideAllMegaMenus(): void {
    this.megaMenuContainers.forEach(container => {
      container.classList.remove('opacity-100', 'visible');
      container.classList.add('opacity-0', 'invisible');
    });
    this.activeMegaMenu = null;
  }

  /**
   * Get the actual header bottom position, accounting for admin bar
   */
  private getHeaderBottom(): number {
    if (!this.header) return 0;
    
    const headerRect = this.header.getBoundingClientRect();
    return headerRect.bottom;
  }

  /**
   * Position mobile menu based on header position
   */
  private positionMobileMenu(): void {
    const headerBottom = this.getHeaderBottom();
    
    if (this.mobileMenu) {
      this.mobileMenu.style.top = `${headerBottom}px`;
    }
  }

  private initStickyHeader(): void {
    let lastScrollTop = 0;
    const headerHeight = this.header?.offsetHeight || 0;
    
    const computedStyle = getComputedStyle(document.documentElement);
    const wpBreakpoint = parseInt(computedStyle.getPropertyValue('--wp-admin-bar-breakpoint')) || 782;
    const adminBarHeight = this.body.classList.contains('admin-bar') ? 
      (window.innerWidth <= wpBreakpoint ? 
        parseInt(computedStyle.getPropertyValue('--admin-bar-height-mobile')) || 46 : 
        parseInt(computedStyle.getPropertyValue('--admin-bar-height')) || 32
      ) : 0;

    window.addEventListener('scroll', () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      if (scrollTop > adminBarHeight) {
        this.body.classList.add('has-scrolled');
      } else {
        this.body.classList.remove('has-scrolled');
      }
      
      if (scrollTop > headerHeight) {
        this.header?.classList.add('is-sticky');
        this.body.classList.add('has-sticky-header');
      } else {
        this.header?.classList.remove('is-sticky');
        this.body.classList.remove('has-sticky-header');
      }
      
      lastScrollTop = scrollTop;
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new Header());
} else {
  new Header();
}